import { useEffect, useSyncExternalStore } from 'react'
import type { Ik, Mens, Vestiging } from '../../gedeeld/types'
import { api } from './api'

/* ===========================================================================
 *  Wat de hele app deelt
 *
 *  Dezelfde kleine winkel als in Projects: een object, een lijst
 *  luisteraars, en useSyncExternalStore. Wat erin staat is wat bijna elk
 *  scherm nodig heeft: wie je bent, of je IT bent, de vestigingen, en (voor
 *  IT) wie er werken.
 *
 *  De tickets, codes en spullen staan er NIET in: die horen bij een scherm,
 *  en worden daar opgehaald. Een winkel met alle codes erin zou een winkel
 *  zijn die codes bewaart terwijl niemand kijkt.
 * =========================================================================== */

interface Staat {
  ik: Ik | null
  /** Heeft deze persoon het recht it.beheren? Het antwoord van de database. */
  beheerder: boolean
  /** Ontwikkelaar of management: dan is er het scherm Instellingen. De functie kijkt zelf opnieuw. */
  instellingen: boolean
  /** Ontwikkelaar: namens kijken, meekijken en de kijk-sessies (0244). Alleen voor de knoppen; de functie kijkt zelf. */
  ontwikkelaar: boolean
  /** Staat IT_KLUIS_SLEUTEL op de server? Zo niet, dan kan IT niets in de kluis zetten. */
  kluis: boolean
  vestigingen: Vestiging[]
  mensen: Mens[]
  meldingen: { id: number; soort: 'ok' | 'fout' | 'info'; tekst: string }[]
  zoekOpen: boolean
  /** Het venster "Probleem melden", met wat er al ingevuld kan worden. */
  melden: { itemId?: string | null; itemNaam?: string | null; systeemId?: string | null; categorie?: string } | null
  /** Telt op als ergens iets verandert dat een lijst op een ander scherm raakt. */
  versie: number
  /** De Worker vraagt om te bevestigen dat jij het bent (punt 11, 0166). */
  bevestigen: boolean
  /** Waarom, zoals de Worker het zegt: null voor een code, 'namens' voor namens kijken. */
  bevestigenWaarom: string | null
}

let staat: Staat = {
  ik: null,
  beheerder: false,
  instellingen: false,
  ontwikkelaar: false,
  kluis: true,
  vestigingen: [],
  mensen: [],
  meldingen: [],
  zoekOpen: false,
  melden: null,
  versie: 0,
  bevestigen: false,
  bevestigenWaarom: null,
}

const luisteraars = new Set<() => void>()

export function zet(deel: Partial<Staat>) {
  staat = { ...staat, ...deel }
  for (const l of luisteraars) l()
}

export function lees(): Staat { return staat }

function abonneer(l: () => void) {
  luisteraars.add(l)
  return () => { luisteraars.delete(l) }
}

export function useStaat<T>(kies: (s: Staat) => T): T {
  return useSyncExternalStore(abonneer, () => kies(staat))
}

/** Er veranderde iets; schermen die useVersie() lezen halen opnieuw op. */
export const gewijzigd = () => zet({ versie: staat.versie + 1 })
export const useVersie = () => useStaat((s) => s.versie)

/* ------------------------------------------------------------------ *
 *  Meldingen in beeld
 * ------------------------------------------------------------------ */

let volgnummer = 0

export function meld(tekst: string, soort: 'ok' | 'fout' | 'info' = 'ok') {
  const id = ++volgnummer
  zet({ meldingen: [...staat.meldingen, { id, soort, tekst }].slice(-4) })
  setTimeout(() => zet({ meldingen: staat.meldingen.filter((m) => m.id !== id) }), soort === 'fout' ? 8000 : 3200)
}

/* Een 428 heeft een eigen venster (bevestigen); daar hoort geen tweede melding bij. */
export const meldFout = (e: unknown) => {
  if (e && typeof e === 'object' && (e as { status?: number }).status === 428) return
  meld(e instanceof Error ? e.message : String(e), 'fout')
}

/* ------------------------------------------------------------------ *
 *  Mensen (alleen voor IT)
 * ------------------------------------------------------------------ */

let mensenBezig: Promise<void> | null = null

export function laadMensen(opnieuw = false): Promise<void> {
  if (!staat.beheerder) return Promise.resolve()
  if (mensenBezig && !opnieuw) return mensenBezig
  mensenBezig = api.mensen()
    .then((mensen) => zet({ mensen }))
    .catch((e) => { mensenBezig = null; meldFout(e) })
  return mensenBezig
}

export function useMensen(): Mens[] {
  const mensen = useStaat((s) => s.mensen)
  const beheerder = useStaat((s) => s.beheerder)
  useEffect(() => { if (beheerder) void laadMensen() }, [beheerder])
  return mensen
}

export function useVestigingen(): Vestiging[] {
  return useStaat((s) => s.vestigingen)
}

export const vestigingNaam = (id: string | null | undefined) =>
  id ? (staat.vestigingen.find((v) => v.id === id)?.naam ?? null) : null

export const mensNaam = (id: string | null | undefined) =>
  id ? (staat.mensen.find((m) => m.id === id)?.naam ?? null) : null

export const meldProbleem = (v: Staat['melden'] = {}) => zet({ melden: v ?? {} })
